import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { getMerchantId } from "@app/dashboard/utils/localStorageUtils";
import { Button, SkeletonLoader } from "@unified-checkout/design-system";
import { Xmark } from "@unified-checkout/design-system/icons/index";
import type { IButton } from "libs/design-system/src/types/Button/button";
import { getApiKeys } from "@app/dashboard/services/inner_layout/xero/api-keys";
import { getXeroOrganizations } from "@app/dashboard/services/inner_layout/xero/get-organizations";
import { connectXeroOrganization } from "@app/dashboard/services/inner_layout/xero/connect";
import { Header } from "./Header";
import { Card } from "@app/dashboard/components/inner_layout/Cards/CardWrapper";
import { OrganizationsList } from "./OrganizationsList";
import { ConfigurationSteps } from "./ConfigurationSteps";
import { notify } from "@app/dashboard/utils/notifier";
import { ConnectModal } from "./Modals/connectModal";
import {
  MAX_NOTIFICATION_WIDTH,
  NOTIFICATION_TIMEOUT,
} from "@app/dashboard/constants/transactions";
import styles from "./xero.module.scss";

const MODAL_STATES = {
  REDIRECTING: "redirecting",
  CONFIGURING: "configuring",
  SUCCESS: "success",
  FAILED: "failed",
  DISCONNECT: "disconnect",
} as const;

type ModalState = typeof MODAL_STATES[keyof typeof MODAL_STATES];

export const Xero = () => {
  const { t } = useTranslation();
  const merchantId = getMerchantId();

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isConnectRequestFailed, setIsConnectRequestFailed] = useState(false);
  const [modalState, setModalState] = useState<ModalState>();
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [customUrl, setCustomUrl] = useState("");

  const {
    data: apiKeys,
    isLoading: apiKeysLoading,
    isFetching: apiKeysFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_API_KEYS, merchantId],
    queryFn: () => getApiKeys(),
  })

  const xeroApiKey = apiKeys?.data?.api_key;

  const {
    isLoading: xeroOriganizationLoading,
    isFetching: xeroOrganizationsFetching,
    isError: xeroOrganizationError,
    data: xeroOrganizations,
    refetch: refetchData
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_XERO_ORGANIZATIONS, xeroApiKey],
    queryFn: () => getXeroOrganizations(xeroApiKey),
    enabled: !!xeroApiKey
  })

  const {
    mutate: connectOrganizationMutate,
    isPending: connectOrganizationLoading,
  } = useMutation({
    mutationKey: [QUERY_KEYS.CONNECT_XERO_ORGANIZATION],
    mutationFn: () => connectXeroOrganization(xeroApiKey),
    onSuccess: (resp) => {
      const redirectionUrl = resp.data.authorization_url
      window.location.href = redirectionUrl
    },
    onError: () => {
      setIsConnectRequestFailed(true);
      setModalState(MODAL_STATES.FAILED);
      setIsConnectModalOpen(true);
    }
  }
  )

  useEffect(() => {
    if (xeroOrganizations?.data) {
      const connections = xeroOrganizations.data?.connections?.filter(Boolean);
      setOrganizations(connections);
      setCustomUrl(xeroOrganizations.data?.custom_payment_url);
      if (connections.length === 0) {
      };
    }
  }, [xeroOrganizations?.data]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("status");

    if (status === MODAL_STATES.SUCCESS) {
      setIsConnectModalOpen(false);
      setModalState(MODAL_STATES.SUCCESS);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => {
        setModalState(undefined);
      }, 3000);
    } else if (status === MODAL_STATES.FAILED) {
      setIsConnectModalOpen(false);
      setModalState(MODAL_STATES.FAILED);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const isConnect = organizations.length > 0;

  const isXeroLoading = apiKeysLoading || xeroOriganizationLoading || xeroOrganizationsFetching || apiKeysFetching
  const isApiKeyMissing = !apiKeysLoading && !apiKeysFetching && !xeroApiKey;

  const handleOpenConnectModal = () => {
    setModalState(MODAL_STATES.REDIRECTING);
    setIsConnectRequestFailed(false);
    setIsConnectModalOpen(true);
    connectOrganizationMutate();
  };

  const handleRetryConnect = () => {
    setModalState(MODAL_STATES.REDIRECTING);
    setIsConnectRequestFailed(false);
    setIsConnectModalOpen(true);
    connectOrganizationMutate();
  };

  const handleCloseConnectModal = () => {
    setIsConnectModalOpen(false);
    setIsConnectRequestFailed(false);
    setModalState(undefined);
  };

  const headerProps = {
    data: organizations,
    isLoading: isXeroLoading,
    refetchData: refetchData,
    isConnect: isConnect,
    apiKeys: xeroApiKey,
    onDisconnectSuccess: () => {
      setOrganizations([]);
      setCustomUrl("");
    },
    isDisconnectModalOpen: isDisconnectModalOpen,
    setIsDisconnectModalOpen: setIsDisconnectModalOpen
  };

  const isConnectModalStateOpen =
    modalState === MODAL_STATES.REDIRECTING ||
    modalState === MODAL_STATES.CONFIGURING ||
    modalState === MODAL_STATES.SUCCESS ||
    modalState === MODAL_STATES.FAILED;

  const connectModalState =
    modalState === MODAL_STATES.DISCONNECT ? null : modalState;

  const connectModalProps = {
    isModalOpen: isConnectModalOpen || isConnectModalStateOpen,
    setIsModalOpen: setIsConnectModalOpen,
    isRequestFailed: isConnectRequestFailed,
    modalState: connectModalState,
    onTryAgain: handleRetryConnect,
    onCancel: handleCloseConnectModal,
    isLoading: connectOrganizationLoading
  };

  const mainButtonProps: IButton = {
    color: "primary",
    size: "md",
    text: t('innerLayout.xeroPage.mainButton'),
    onClickHandler: handleOpenConnectModal
  }

  const detailsBoxRenderer = (
    {
      box = undefined,
      height,
      noOfRows,
      key
    }: {
      box: ReactNode,
      height: string,
      noOfRows: number,
      key: number | string
    }
  ): ReactNode => {
    return (
      isXeroLoading
        ?
        <SkeletonLoader
          type="bordered_box"
          noOfRows={noOfRows}
          height={height}
          key={key}
        />
        :
        box
        &&
        <Fragment key={key}>
          {box}
        </Fragment>
    )
  };

  const allBoxes = [
    detailsBoxRenderer({
      box: <Card
        title={t('innerLayout.xeroPage.organizationsList.title')}
      >
        {organizations?.map((value: any, index: number) => {
          return (
            <OrganizationsList value={value} index={index} />
          )
        })}
      </Card>,
      noOfRows: 6,
      height: "20rem",
      key: t('innerLayout.xeroPage.organizationsList.title')
    }),

    detailsBoxRenderer({
      box: <ConfigurationSteps customUrl={customUrl} />,
      noOfRows: 7,
      height: "24rem",
      key: t('innerLayout.xeroPage.configurationSteps.title')
    })
  ];
  // Filter out empty boxes
  const filteredBoxes = allBoxes.filter(Boolean);

  // Split the boxes into two columns
  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0); // Even-indexed items
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0); // Odd-indexed items

  const shouldShowConnectButton = xeroOrganizationError || !isConnect;

  const content = (
    isXeroLoading
      ?
      <div className={styles["xero__main__col"]}>
        {col1Boxes}
        {col2Boxes}
      </div>
      :
      shouldShowConnectButton
        ?
        <div className={styles["xero__connect-button"]}>
          <Button {...mainButtonProps} />
        </div>
        :
        <div className={styles["xero__main__col"]}>
          {col1Boxes}
          {col2Boxes}
        </div>
  );

  useEffect(() => {
    if (isApiKeyMissing) {
      notify.error(t('innerLayout.xeroPage.modals.missingApi'), {
        maxWidth: MAX_NOTIFICATION_WIDTH,
        timeout: NOTIFICATION_TIMEOUT,
        icon: Xmark,
      });
    }
  }, [isApiKeyMissing]);

  return (
    <div className={styles["xero"]}>
      <Header {...headerProps} />
      <ConnectModal {...connectModalProps} />
      <div className={styles["xero__main"]}>
        {content}
      </div>
    </div>
  )
}